import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AIScreeningService } from '../../services/ai-Screening';
import { finalize } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../../shared/confirmation-dialog/confirmation-dialog.component';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';


interface CountryCode {
  name: string;
  code: string;
  dialCode: string;
  flag: string;
}

@Component({
  standalone: true,
  selector: 'app-verification',
  imports: [CommonModule, FormsModule, ReactiveFormsModule,ConfirmationDialogComponent],
  templateUrl: './verification.html',
  styleUrl: './verification.css'

})
export class Verification implements OnInit {
  emailForm!: FormGroup;
  otpForm!: FormGroup;
  step = 1;
  isVerified = false;
  otpBoxes = [1, 2, 3, 4, 5, 6];
  otpString: string[] = ['', '', '', '', '', ''];
  otp: number = 0;
  accessCode: string | null = null;
  screeningId: string = '';
  isLoading: boolean = false;
  resendCount: number = 0;
  resendTimer: number = 0;
  resendInterval: any;
  maxResends: number = 3;
  cooldownPeriod: number = 15 * 60; // 15 minutes in seconds
  get f() { return this.emailForm.controls; }

  countryCodes: CountryCode[] = [
    { name: 'United States', code: 'US', dialCode: '+1', flag: '🇺🇸' },
    { name: 'United Kingdom', code: 'GB', dialCode: '+44', flag: '🇬🇧' },
    { name: 'Canada', code: 'CA', dialCode: '+1', flag: '🇨🇦' },
    { name: 'Australia', code: 'AU', dialCode: '+61', flag: '🇦🇺' },
    { name: 'India', code: 'IN', dialCode: '+91', flag: '🇮🇳' },
    { name: 'Germany', code: 'DE', dialCode: '+49', flag: '🇩🇪' },
    { name: 'France', code: 'FR', dialCode: '+33', flag: '🇫🇷' },
    { name: 'Spain', code: 'ES', dialCode: '+34', flag: '🇪🇸' },
    { name: 'Italy', code: 'IT', dialCode: '+39', flag: '🇮🇹' },
    { name: 'Japan', code: 'JP', dialCode: '+81', flag: '🇯🇵' },
    { name: 'China', code: 'CN', dialCode: '+86', flag: '🇨🇳' },
    { name: 'Brazil', code: 'BR', dialCode: '+55', flag: '🇧🇷' },
    { name: 'Mexico', code: 'MX', dialCode: '+52', flag: '🇲🇽' },
    { name: 'UAE', code: 'AE', dialCode: '+971', flag: '🇦🇪' },
    { name: 'Saudi Arabia', code: 'SA', dialCode: '+966', flag: '🇸🇦' },
    { name: 'Pakistan', code: 'PK', dialCode: '+92', flag: '🇵🇰' },
    { name: 'South Africa', code: 'ZA', dialCode: '+27', flag: '🇿🇦' },
    { name: 'Singapore', code: 'SG', dialCode: '+65', flag: '🇸🇬' },
    { name: 'Malaysia', code: 'MY', dialCode: '+60', flag: '🇲🇾' },
    { name: 'Philippines', code: 'PH', dialCode: '+63', flag: '🇵🇭' }
  ];

  constructor(private dialog: MatDialog,private router: Router, private cdr: ChangeDetectorRef, private fb: FormBuilder, private toastr: ToastrService, private aiservice: AIScreeningService, private route: ActivatedRoute, private authService: AuthServiceService) {
    this.emailForm = this.fb.group({
      name: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        Validators.pattern(/^[a-zA-Z\s.\-]+$/)
      ]],
      email: ['', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(254),
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-z]{2,}$/)
      ]],
      countryCode: ['+91', Validators.required],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9]{10}$/)]]
    });

    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]]
    });
  }
  organizationName: string = '';
  logoUrl: string | null = null;
  isMobileDevice(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    // 1. Standard mobile User-Agent check (handles normal mobile browsers)
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    
    // 2. iOS devices (iPhone/iPad) in "Desktop site" mode
    // Spoofs as Macintosh/MacIntel and has multi-touch support (> 1)
    const isIPadOS = (navigator.platform === 'MacIntel' || userAgent.includes('Macintosh')) && navigator.maxTouchPoints > 1;
    

    // 3. Android devices (phone/tablet) in "Desktop site" mode
    // Spoofs as Linux, has multi-touch support (> 1), and is not ChromeOS
    const isAndroidDesktopMode = navigator.maxTouchPoints > 1 &&
      userAgent.includes('Linux') &&
      !userAgent.includes('CrOS');

    // 4. Physical hardware screen dimensions check
    // Width < 768 is a phone/tablet in portrait.
    // If height < 768, we check if it is in portrait mode (width < height) to prevent blocking small laptops (e.g. 1280x720).
    const isSmallScreen = window.screen && (
      window.screen.width < 768 || 
      (window.screen.height < 768 && window.screen.width < window.screen.height)
    );

    return isMobileUA || isIPadOS || isAndroidDesktopMode || isSmallScreen;
  }

  checkDeviceAndBlock(): void {
    if (this.isMobileDevice()) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        disableClose: true,
        data: {
          title: 'Desktop Or Laptop Required',
          message: 'This interview must be taken on a desktop or laptop computer. Mobile, iPad, and tablet access is not supported.',
          confirmText: 'Copy Link',
          cancelText: 'Close',
          type: 'warning'
        }
      });

      dialogRef.afterClosed().subscribe((shouldCopy: boolean) => {
        if (shouldCopy) {
          navigator.clipboard.writeText(window.location.href).then(() => {
            this.toastr.success('Link copied! Please open it on a desktop browser.');
          });
        }
        if (this.accessCode && this.screeningId) {
          this.router.navigate(['/interview.ai', this.accessCode, this.screeningId]);
        } else {
          this.router.navigate(['/']);
        }
      });
    }
  }

  ngOnInit(): void {
    this.accessCode = this.route.snapshot.paramMap.get('accessCode') || '';
    const idParam = this.route.snapshot.paramMap.get('id');
    this.screeningId = idParam || '';

    this.checkDeviceAndBlock();

    this.route.paramMap.subscribe(params => {
      this.accessCode = params.get('accessCode') || '';
      const innerIdParam = this.route.snapshot.paramMap.get('id');
      this.screeningId = innerIdParam || '';
    });

    if (!this.accessCode) {
      this.toastr.warning('Access code not found. Please open the link from your email.');
    } else {
      sessionStorage.setItem('screening_access_code', this.accessCode);
    }
    this.initResendState();
    this.getRoundDetails();

    const isStudentDashboard = this.route.snapshot.queryParamMap.get('isStudentDashboard') === 'true';
    if (isStudentDashboard) {
      const userId = localStorage.getItem('user_id');
      if (userId && this.accessCode) {
        this.isLoading = true;
        this.authService.getUserDetails(userId).subscribe({
          next: (res) => {
            if (res.Success) {
              const user = res.Data;
              let name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
              if (!name) name = 'Student User';

              const email = user.phone_email || '';
              let rawPhone = String(user.phone_number || '0000000000').replace(/\D/g, '');
              const phoneNum = rawPhone.length >= 10 ? rawPhone.slice(-10) : rawPhone.padEnd(10, '0');
              const phone = phoneNum;
              
              if (email) {
                // Patch form to ensure Step 2 displays the email correctly
                this.emailForm.patchValue({ 
                  email: email, 
                  name: name,
                  phone: phoneNum,
                  countryCode: '+91'
                });

                this.aiservice.sendOTP(this.accessCode!, this.screeningId, email, name, phone).pipe(finalize(() => {
                  this.isLoading = false;
                  this.cdr.detectChanges();
                })).subscribe({
                  next: (response) => {
                    if (response?.success ?? (response as any)?.Success) {
                      this.toastr.success('OTP sent successfully!');
                      this.otpString = ['', '', '', '', '', ''];
                      this.otp = 0;
                      this.otpForm.reset();
                      this.step = 2;
                      this.resetResendState();
                      this.cdr.detectChanges();
                    } else {
                      this.toastr.error('Failed to auto-send OTP. Please fill the details manually.');
                      this.step = 1;
                    }
                  },
                  error: (err) => {
                    console.error('Error auto-sending OTP:', err);
                    this.toastr.error('Failed to auto-send OTP. Please fill the details manually.');
                    this.step = 1;
                  }
                });
              } else {
                this.isLoading = false;
                this.cdr.detectChanges();
                this.toastr.error('Profile email is missing. Please enter your details.');
              }
            } else {
              this.isLoading = false;
              this.cdr.detectChanges();
            }
          },
          error: () => {
            this.isLoading = false;
            this.cdr.detectChanges();
          }
        });
      }
    }
  }

  getRoundDetails() {
    const cachedName = sessionStorage.getItem('organization_name');
    const cachedMultilanguage = sessionStorage.getItem('enable_multilanguage');
    if (cachedName && cachedMultilanguage) {
      this.organizationName = cachedName;
      this.cdr.detectChanges();
      return;
    }

    const accessCode = sessionStorage.getItem('screening_access_code');
    if (!this.screeningId) return;

    const request$ = accessCode
      ? this.aiservice.getPublicScreeningRoundDetails(accessCode, this.screeningId)
      : this.aiservice.getScreeningRoundDetails(this.screeningId);

    request$.subscribe({
      next: (res) => {
        if (res?.success ?? (res as any)?.Success) {
          const tenant = (res.data ?? (res as any).Data)?.tenant;
          const round = (res.data ?? (res as any).Data)?.round;
          this.organizationName = tenant?.organization_name || '';
          this.logoUrl = round?.logo_url || null;
          
          if (this.organizationName) sessionStorage.setItem('organization_name', this.organizationName);
          if (this.logoUrl) sessionStorage.setItem('logo_url', this.logoUrl);
          if (round) {
            sessionStorage.setItem('enable_multilanguage', (round as any).enable_multilanguage ? 'true' : 'false');
          }
          
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        console.error('Error fetching organization details:', err);
        this.organizationName = 'AI Interview Platform';
        this.cdr.detectChanges();
      }
    });
  }

  initResendState() {
    const savedState = sessionStorage.getItem(`resend_state_${this.screeningId}`);
    if (savedState) {
      const state = JSON.parse(savedState);
      this.resendCount = state.resendCount || 0;
      const lastResendTime = state.lastResendTime || 0;
      const currentTime = Math.floor(Date.now() / 1000);
      const elapsed = currentTime - lastResendTime;

      if (elapsed < this.cooldownPeriod) {
        this.resendTimer = this.cooldownPeriod - elapsed;
        this.startCountdown();
      }
    }
  }

  saveResendState() {
    const state = {
      resendCount: this.resendCount,
      lastResendTime: Math.floor(Date.now() / 1000)
    };
    sessionStorage.setItem(`resend_state_${this.screeningId}`, JSON.stringify(state));
  }

  startCountdown() {
    if (this.resendInterval) clearInterval(this.resendInterval);
    this.resendInterval = setInterval(() => {
      if (this.resendTimer > 0) {
        this.resendTimer--;
        this.cdr.detectChanges();
      } else {
        clearInterval(this.resendInterval);
      }
    }, 1000);
  }

  formatTimer(): string {
    const minutes = Math.floor(this.resendTimer / 60);
    const seconds = this.resendTimer % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  resendOTP() {
    if (this.resendCount >= this.maxResends) {
      this.toastr.error('Maximum OTP resend attempts reached.');
      return;
    }

    if (this.resendTimer > 0) {
      this.toastr.warning(`Please wait ${this.formatTimer()} before requesting a new OTP.`);
      return;
    }

    if (!this.accessCode) {
      this.toastr.error('Cannot resend OTP without access code.');
      return;
    }

    this.isLoading = true;
    const email = this.emailForm.value.email;

    this.aiservice.resendOTP(this.accessCode, this.screeningId, email).pipe(finalize(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    })).subscribe({
      next: (response) => {
        if (response?.success ?? (response as any)?.Success) {
          this.toastr.success('OTP resent successfully!');
          this.otpString = ['', '', '', '', '', ''];
          this.otp = 0;
          this.otpForm.reset();
          this.resendCount++;
          this.resendTimer = this.cooldownPeriod;
          this.saveResendState();
          this.startCountdown();
          this.cdr.detectChanges();
        } else {
          const msg = response?.message || (response as any)?.Message || 'Failed to resend OTP. Please try again.';
          this.toastr.error(msg);
        }
      },
      error: (err) => {
        console.error('Error resending OTP:', err);
        const Message = err?.error?.message || err?.message || 'Something went wrong.';
        this.toastr.error(Message);
      }
    });
  }

  onOtpInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;

    let value = input.value.replace(/\D/g, '');

    if (value.length > 1) {
      value = value.slice(0, 1);
    }

    input.value = value;
    this.otpString[index] = value;

    this.updateOtpNumber();

    const fullOtp = this.otpString.join('');
    this.otpForm.get('otp')?.setValue(fullOtp, { emitEvent: false });

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`) as HTMLInputElement;
      nextInput?.focus();
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace' && !input.value && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`) as HTMLInputElement;
      prevInput?.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const pastedText = clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, 6);

    if (digits) {
      for (let i = 0; i < digits.length; i++) {
        this.otpString[i] = digits[i];
      }
      this.updateOtpNumber();
      const fullOtp = this.otpString.join('');
      this.otpForm.get('otp')?.setValue(fullOtp, { emitEvent: false });

      // Focus the next appropriate input box
      const nextIndex = Math.min(digits.length, 5);
      const nextInput = document.getElementById(`otp-${nextIndex}`) as HTMLInputElement;
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  onPhoneInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/[^0-9]/g, '');

    if (value.length > 10) {
      value = value.slice(0, 10);
    }

    input.value = value;
    this.emailForm.get('phone')?.setValue(value, { emitEvent: false });
  }

  onNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    // Keep only letters, spaces, dots, and hyphens
    let value = input.value.replace(/[^a-zA-Z\s.\-]/g, '');

    if (value.length > 50) {
      value = value.slice(0, 50);
    }

    // Update the input field only if the value was modified
    if (input.value !== value) {
      input.value = value;
    }
    this.emailForm.get('name')?.setValue(value, { emitEvent: false });
  }

  onEmailInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    if (value.length > 254) {
      value = value.slice(0, 254);
      input.value = value;
      this.emailForm.get('email')?.setValue(value, { emitEvent: false });
    }
  }

  verifiedCandidateId: number | null = null;
  verifiedSessionId: number | null = null;

  verifyOTP() {
    if (!this.accessCode) {
      this.toastr.error('Cannot Verify OTP without access code.');
      return;
    }

    if (this.otpForm.invalid) {
      return;
    }
    this.isLoading = true;
    const email = this.emailForm.value.email;
    const OTP = this.otpForm.value.otp;

    this.aiservice.verifyOTP(this.accessCode, this.screeningId, email, OTP).pipe(finalize(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    })).subscribe({
      next: (response) => {
        if (response?.success ?? (response as any)?.Success) {
          const candidateId = (response?.data ?? (response as any)?.Data)?.candidate_id ?? null;
          const sessionId = (response?.data ?? (response as any)?.Data)?.session_id ?? null;
          const aiSessionToken = (response?.data ?? (response as any)?.Data)?.ai_session_token ?? null;

          if (aiSessionToken) {
            sessionStorage.setItem('ai_session_token', aiSessionToken);
          }

          const screeningRound = (response?.data ?? (response as any)?.Data)?.screening_round;
          if (screeningRound) {
            sessionStorage.setItem('enable_multilanguage', screeningRound.enable_multilanguage ? 'true' : 'false');
          }

          const countryCode = this.emailForm.value.countryCode || '';
          const phoneNumber = this.emailForm.value.phone || '';
          sessionStorage.setItem('candidate_info', JSON.stringify({
            name: this.emailForm.value.name,
            email: this.emailForm.value.email,
            phone: phoneNumber
          }));

          this.verifiedCandidateId = candidateId;
          this.verifiedSessionId = sessionId;
          this.isVerified = true;
          this.toastr.success('OTP verified successfully!');
          this.continueToInterview();
        } else {
          const msg = response?.message || (response as any)?.Message || 'Failed to verify OTP. Please try again.';
          this.toastr.error(msg);
        }
      },
      error: (err) => {
        console.error('Verify OTP error:', err);
        const Message =
          err?.error?.message ||
          err?.error?.error?.message ||
          err?.message;
        this.toastr.error(Message ?? 'Something went wrong. Please try again.');
      }
    });
  }
  sendOTP() {
    if (!this.accessCode) {
      this.toastr.error('Cannot send OTP without access code.');
      return;
    }

    if (this.emailForm.invalid) {
      this.toastr.error('Please enter a valid name and email.');
      return;
    }
    this.isLoading = true;
    const email = this.emailForm.value.email;
    const name = this.emailForm.value.name;
    const countryCode = this.emailForm.value.countryCode;
    const phoneNumber = this.emailForm.value.phone;
    const phone = phoneNumber; // Send just the phone number without country code

    this.aiservice.sendOTP(this.accessCode, this.screeningId, email, name, phone).pipe(finalize(() => {
      this.isLoading = false;
      this.cdr.detectChanges();
    })).subscribe({
      next: (response) => {
        if (response?.success ?? (response as any)?.Success) {
          this.toastr.success('OTP sent successfully!');
          this.otpString = ['', '', '', '', '', ''];
          this.otp = 0;
          this.otpForm.reset();
          this.step = 2;
          this.resetResendState();
          this.cdr.detectChanges();
        } else {
          const msg = response?.message || (response as any)?.Message || 'Failed to send OTP. Please try again.';
          this.toastr.error(msg);
        }
      },
      error: (err) => {
        console.error('Error sending OTP:', err);
        const Message =
          err?.error?.message ||
          err?.error?.error?.message ||
          err?.message;
        this.toastr.error(Message ?? 'Something went wrong. Please try again.');
      }
    });
  }

  resetResendState() {
    if (this.resendInterval) clearInterval(this.resendInterval);
    this.resendCount = 0;
    this.resendTimer = 0;
    sessionStorage.removeItem(`resend_state_${this.screeningId}`);
  }

  goBackToStep1() {
    this.step = 1;
    this.otpString = ['', '', '', '', '', ''];
    this.otp = 0;
    this.otpForm.reset();
    this.isVerified = false;
    this.cdr.detectChanges();
  }

  handleBack() {
    this.step = 1;
    this.otpString = ['', '', '', '', '', ''];
    this.otp = 0;
    this.otpForm.reset();
    this.isVerified = false;
    this.cdr.detectChanges();
    this.router.navigate(['/interview.ai', this.accessCode, this.screeningId]);
  }

  private updateOtpNumber() {
    const otpValue = this.otpString.join('').replace(/\D/g, '');
    this.otp = otpValue ? parseInt(otpValue, 10) : 0;
  }

  continueToInterview() {
    if (this.verifiedCandidateId && this.verifiedSessionId && this.screeningId) {
      this.router.navigate([
        '/resume-upload',
        this.verifiedCandidateId,
        this.verifiedSessionId,
        this.screeningId
      ]);
    } else {
      this.toastr.error('Missing session details. Please verify your OTP again.');
    }
  }
}
